import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  FaCalendarAlt, FaCheckCircle, FaUsers, FaHandsHelping, FaRegEnvelope, 
  FaArrowRight, FaVideo, FaChevronLeft, FaChevronRight, FaSpinner,
  FaImage, FaInfoCircle, FaExclamationTriangle, FaCalendarDay
} from 'react-icons/fa';
import { MdOutlineBusinessCenter, MdAttachMoney, MdTrendingUp, MdSupportAgent } from 'react-icons/md';
import Footer from '../components/Footer';
import ReCAPTCHA from 'react-google-recaptcha';

function MasaKalptaruPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    businessName: '',
    aboutBusiness: '',
    queryType: 'general',
    registrationDate: new Date().toISOString().split('T')[0]
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // API Data State
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageUrls, setImageUrls] = useState({
    banners: [],
    aboutIcon: null,
    helpIcons: [],
    howItWorksIcons: []
  });

  // reCAPTCHA state
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);

  const API_URL = `${process.env.REACT_APP_API_BASE_URL}/UIMasaKalptaru`;
  const API_MEDIA = `${process.env.REACT_APP_API_BASE_URL}/UImedia`;
  const REGISTRATION_API = `${process.env.REACT_APP_API_BASE_URL}/masaKalptaruRegistration`;

  // reCAPTCHA keys
  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY ;

  // Fetch API Data
  useEffect(() => {
    fetchMasaKalptaruData();
  }, []);

  // Format date for display
  const formatDisplayDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Single image loader function
  const loadSingleImage = async (imageId, token) => {
    if (!imageId) return null;
    
    try {
      const imgRes = await axios.get(
        `${API_MEDIA}/${imageId}/download`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          responseType: 'blob',
          timeout: 10000
        }
      );
      return URL.createObjectURL(imgRes.data);
    } catch (error) {
      console.error('Error loading image:', error);
      return null;
    }
  };

  const fetchMasaKalptaruData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(API_URL, {
        params: { isActive: true },
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      let data = response.data;
      if (Array.isArray(data)) {
        data = data.find(item => item.isActive) || data[0] || null;
      }
      
      if (!data) {
        throw new Error('No active MASA Kalptaru data found');
      }

      setApiData(data);
      await loadAllImages(data, token);
      
      if (data.queryTypes && data.queryTypes.length > 0) {
        setFormData(prev => ({
          ...prev,
          queryType: data.queryTypes[0].value || 'general'
        }));
      }
      
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAllImages = async (data, token) => {
    const bannerUrls = [];
    for (let i = 0; i < (data.banners || []).length; i++) {
      const banner = data.banners[i];
      if (banner.image?._id || banner.image?.id) {
        const imageId = banner.image._id || banner.image.id;
        const imageUrl = await loadSingleImage(imageId, token);
        bannerUrls[i] = imageUrl;
      }
    }

    let aboutIconUrl = null;
    if (data.aboutSection?.icon?._id || data.aboutSection?.icon?.id) {
      const iconId = data.aboutSection.icon._id || data.aboutSection.icon.id;
      aboutIconUrl = await loadSingleImage(iconId, token);
    }

    const helpIconUrls = [];
    for (let i = 0; i < (data.helpSections || []).length; i++) {
      const section = data.helpSections[i];
      if (section.icon?._id || section.icon?.id) {
        const iconId = section.icon._id || section.icon.id;
        const iconUrl = await loadSingleImage(iconId, token);
        helpIconUrls[i] = iconUrl;
      }
    }

    const howItWorksIconUrls = [];
    for (let i = 0; i < (data.howItWorks || []).length; i++) {
      const step = data.howItWorks[i];
      if (step.icon?._id || step.icon?.id) {
        const iconId = step.icon._id || step.icon.id;
        const iconUrl = await loadSingleImage(iconId, token);
        howItWorksIconUrls[i] = iconUrl;
      }
    }

    setImageUrls({
      banners: bannerUrls,
      aboutIcon: aboutIconUrl,
      helpIcons: helpIconUrls,
      howItWorksIcons: howItWorksIconUrls
    });
  };

  const nextSlide = () => {
    if (!apiData?.banners) return;
    setCurrentSlide((prev) => (prev === apiData.banners.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    if (!apiData?.banners) return;
    setCurrentSlide((prev) => (prev === 0 ? apiData.banners.length - 1 : prev - 1));
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear error when user starts typing
    if (submitError) setSubmitError('');
  };

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  const handleRecaptchaExpired = () => {
    setRecaptchaToken(null);
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  };

  const handleRecaptchaError = (error) => {
    console.error('reCAPTCHA Error:', error);
    setRecaptchaToken(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form fields
    if (!formData.name.trim()) {
      setSubmitError('Please enter your name');
      return;
    }
    
    if (!formData.email.trim()) {
      setSubmitError('Please enter your email');
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSubmitError('Please enter a valid email address');
      return;
    }
    
    // Mobile validation
    const mobileRegex = /^[0-9]{10}$/;
    if (!mobileRegex.test(formData.mobile)) {
      setSubmitError('Please enter a valid 10-digit mobile number');
      return;
    }
    
    if (!formData.businessName.trim()) {
      setSubmitError('Please enter your business name');
      return;
    }
    
    if (!formData.aboutBusiness.trim()) {
      setSubmitError('Please describe your business');
      return;
    }
    
    // Validate reCAPTCHA
    // if (!recaptchaToken) {
    //   setSubmitError('Please complete the reCAPTCHA verification');
    //   return;
    // }
    
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      // Prepare data with reCAPTCHA token
      const registrationData = {
        ...formData,
        recaptchaToken
      };
      
      // Send registration data to backend
      const response = await axios.post(REGISTRATION_API, registrationData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.success) {
        setSubmitSuccess(true);
        
        // Reset reCAPTCHA
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
        setRecaptchaToken(null);
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          mobile: '',
          businessName: '',
          aboutBusiness: '',
          queryType: apiData?.queryTypes?.[0]?.value || 'general',
          registrationDate: new Date().toISOString().split('T')[0]
        });
        
        // Reset success message after 5 seconds
        setTimeout(() => {
          setSubmitSuccess(false);
        }, 5000);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      
      // Reset reCAPTCHA on error
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      setRecaptchaToken(null);
      
      if (error.response) {
        const errorMessage = error.response.data.message || 'Registration failed. Please try again.';
        setSubmitError(errorMessage);
      } else if (error.request) {
        setSubmitError('Network error. Please check your connection.');
      } else {
        setSubmitError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Auto slide for banners
  useEffect(() => {
    if (!apiData?.banners || apiData.banners.length <= 1) return;
    
    const interval = setInterval(() => {
      nextSlide();
    }, 5000);
    
    return () => clearInterval(interval);
  }, [apiData?.banners, currentSlide]);

  // Cleanup image URLs on unmount
  useEffect(() => {
    return () => {
      imageUrls.banners.forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });
      
      if (imageUrls.aboutIcon) {
        URL.revokeObjectURL(imageUrls.aboutIcon);
      }
      
      imageUrls.helpIcons.forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });
      
      imageUrls.howItWorksIcons.forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [imageUrls]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-800 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="animate-spin text-amber-500 text-4xl mx-auto mb-4" />
          <p className="text-white text-lg">Loading MASA Kalptaru...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-800 flex items-center justify-center">
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 p-8 rounded-2xl border border-red-500/20 max-w-md text-center">
          <FaExclamationTriangle className="text-red-500 text-5xl mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Unable to Load Content</h2>
          <p className="text-gray-300 mb-6">{error}</p>
          <button 
            onClick={fetchMasaKalptaruData}
            className="bg-gradient-to-r from-amber-500 to-yellow-500 text-gray-900 font-bold px-6 py-3 rounded-full hover:from-amber-600 hover:to-yellow-600 transition-all"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!apiData) {
    return (
      <div className="min-h-screen bg-gray-800 flex items-center justify-center">
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 p-8 rounded-2xl border border-amber-500/20 max-w-md text-center">
          <FaInfoCircle className="text-amber-500 text-5xl mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">No Data Available</h2>
          <p className="text-gray-300">MASA Kalptaru content is currently unavailable. Please check back later.</p>
        </div>
      </div>
    );
  }

  const data = apiData;

  return (
    <div className="min-h-screen bg-gray-800">
      
      {/* Banner Slider Section */}
      {data.banners && data.banners.length > 0 && (
        <div className="relative h-[500px] w-full overflow-hidden">
          {data.banners.map((banner, index) => (
            <div
              key={banner._id || index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
              }`}
            >
              {imageUrls.banners[index] ? (
                <div 
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: `url(${imageUrls.banners[index]})` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 to-gray-900/60"></div>
                </div>
              ) : (
                <div className="absolute inset-0 bg-gradient-to-r from-gray-900 to-gray-800 flex items-center justify-center">
                  <div className="text-center">
                    <FaImage className="text-gray-600 text-6xl mb-4" />
                    <p className="text-gray-400 text-xl">Image Loading...</p>
                  </div>
                </div>
              )}
              
              <div className="relative h-full flex items-center">
                <div className="max-w-6xl mx-auto px-4 w-full">
                  <div className="text-white max-w-2xl">
                    <h1 className="text-5xl md:text-6xl font-bold mb-4 bg-gradient-to-r from-amber-400 to-yellow-500 bg-clip-text text-transparent">
                      {banner.title || 'MASA Kalptaru'}
                    </h1>
                    <p className="text-2xl md:text-3xl font-semibold mb-6">
                      {banner.subtitle || 'Expert Guidance for MSMEs'}
                    </p>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2 bg-amber-500/20 px-4 py-2 rounded-full border border-amber-500/30">
                        <FaCalendarAlt className="text-amber-400" />
                        <span className="text-amber-300">
                          {data.sessionDetails?.day || 'Every Thursday'} | {data.sessionDetails?.platform || 'Google Meet'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 bg-green-500/20 px-4 py-2 rounded-full border border-green-500/30">
                        <FaCheckCircle className="text-green-400" />
                        <span className="text-green-300">{data.sessionDetails?.cost || '100% Free'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
          
          {/* Slider Controls */}
          {data.banners.length > 1 && (
            <>
              <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 bg-gray-900/70 hover:bg-gray-900 text-amber-400 p-3 rounded-full transition-all hover:scale-110"
                aria-label="Previous slide"
              >
                <FaChevronLeft />
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 bg-gray-900/70 hover:bg-gray-900 text-amber-400 p-3 rounded-full transition-all hover:scale-110"
                aria-label="Next slide"
              >
                <FaChevronRight />
              </button>
              
              {/* Slider Indicators */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20 flex gap-2">
                {data.banners.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      index === currentSlide 
                        ? 'bg-amber-400 scale-125' 
                        : 'bg-gray-400 hover:bg-gray-300'
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 py-8">
        
        {/* About Section */}
        {data.aboutSection && (
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl shadow-2xl p-8 mb-8 border border-amber-500/20">
            <div className="flex items-center gap-3 mb-6">
              {imageUrls.aboutIcon ? (
                <div className="p-3 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-full">
                  <img 
                    src={imageUrls.aboutIcon} 
                    alt="About Icon"
                    className="w-10 h-10 object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentNode.innerHTML = '<FaHandsHelping class="text-2xl text-white" />';
                    }}
                  />
                </div>
              ) : (
                <div className="p-3 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-full">
                  <FaHandsHelping className="text-2xl text-white" />
                </div>
              )}
              <h2 className="text-3xl font-bold text-white">
                {data.aboutSection.title || 'About MASA Kalptaru'}
              </h2>
            </div>
            <p className="text-gray-300 text-lg leading-relaxed">
              {data.aboutSection.description || 'MASA Kalptaru is a free weekly online helpdesk for MSMEs and startups.'}
            </p>
          </div>
        )}

        {/* What We Help With Section */}
        {data.helpSections && data.helpSections.length > 0 && (
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">What We Help With</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {data.helpSections.map((item, index) => (
                <div key={item._id || index} className="bg-gradient-to-b from-gray-900 to-gray-800 p-6 rounded-xl shadow-xl border border-amber-500/20 hover:border-amber-500/50 transition-all hover:scale-105">
                  <div className="flex items-center gap-3 mb-4">
                    {imageUrls.helpIcons[index] ? (
                      <div className="p-2 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-lg">
                        <img 
                          src={imageUrls.helpIcons[index]} 
                          alt={item.title}
                          className="w-5 h-5 object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentNode.innerHTML = '<MdOutlineBusinessCenter class="text-xl text-white" />';
                          }}
                        />
                      </div>
                    ) : (
                      <div className="p-2 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-lg">
                        <MdOutlineBusinessCenter className="text-xl text-white" />
                      </div>
                    )}
                    <h3 className="font-bold text-white">{item.title || 'Help Section'}</h3>
                  </div>
                  <p className="text-gray-400">{item.description || 'Get expert guidance'}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* How It Works Section */}
        {data.howItWorks && data.howItWorks.length > 0 && (
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 mb-12 border border-amber-500/20">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">How It Works</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {data.howItWorks.map((item, index) => (
                <div key={item._id || item.step} className="relative text-center">
                  {imageUrls.howItWorksIcons[index] ? (
                    <div className="w-20 h-20 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <img 
                        src={imageUrls.howItWorksIcons[index]} 
                        alt={`Step ${item.step}`}
                        className="w-10 h-10 object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentNode.innerHTML = `<span class="text-2xl text-white font-bold">${item.step}</span>`;
                        }}
                      />
                    </div>
                  ) : (
                    <div className="w-20 h-20 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <span className="text-2xl text-white font-bold">{item.step}</span>
                    </div>
                  )}
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-gray-800 text-amber-400 rounded-full flex items-center justify-center text-sm font-bold border border-amber-500">
                    {item.step}
                  </div>
                  <h3 className="font-bold text-lg mb-2 text-white">{item.title || `Step ${item.step}`}</h3>
                  <p className="text-gray-400">{item.desc || 'Complete this step'}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Who Should Join Section */}
        {data.targetAudience && data.targetAudience.length > 0 && (
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Who Should Join</h2>
            <div className="flex flex-wrap justify-center gap-4">
              {data.targetAudience.map((item, index) => (
                <div key={index} className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white px-6 py-3 rounded-full shadow-lg hover:shadow-xl transition-shadow hover:scale-105">
                  <span className="font-bold">{item}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Registration Form Section */}
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl shadow-2xl p-8 mb-12 border border-amber-500/20">
          <div className="text-center mb-8">
            <h2 className="text-4xl font-bold bg-gradient-to-r from-amber-400 to-yellow-500 bg-clip-text text-transparent mb-4">
              {data.formConfig?.title || 'Register for MASA Kalptaru – MSME Helpdesk'}
            </h2>
            <p className="text-xl text-gray-300 mb-6">
              {data.formConfig?.subtitle || '👉 Free guidance. Real solutions. Every Thursday.'}
            </p>
            
            
            
            {submitError && (
              <div className="bg-gradient-to-r from-red-900/30 to-red-800/30 border border-red-500/30 text-red-400 px-6 py-4 rounded-lg mb-6 backdrop-blur-sm">
                <p className="font-bold">Error</p>
                <p>{submitError}</p>
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="max-w-2xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-amber-300 mb-2 font-bold">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full p-3 bg-gray-700 border-2 border-gray-600 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 text-white outline-none transition"
                  placeholder="Enter your full name"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-amber-300 mb-2 font-bold">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full p-3 bg-gray-700 border-2 border-gray-600 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 text-white outline-none transition"
                  placeholder="Enter your email"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-amber-300 mb-2 font-bold">Mobile Number *</label>
                <input
                  type="tel"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  required
                  pattern="[0-9]{10}"
                  maxLength="10"
                  className="w-full p-3 bg-gray-700 border-2 border-gray-600 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 text-white outline-none transition"
                  placeholder="Enter 10-digit mobile number"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-amber-300 mb-2 font-bold">Business Name *</label>
                <input
                  type="text"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleChange}
                  required
                  className="w-full p-3 bg-gray-700 border-2 border-gray-600 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 text-white outline-none transition"
                  placeholder="Enter your business name"
                  disabled={isSubmitting}
                />
              </div>

              {/* Registration Date Field */}
              <div className="col-span-full">
                <label className="block text-amber-300 mb-2 font-bold">Registration Date</label>
                <div className="relative">
                  <div className="flex items-center gap-3 p-3 bg-gray-700 border-2 border-gray-600 rounded-lg">
                    <FaCalendarDay className="text-amber-400 text-xl" />
                    <input
                      type="date"
                      name="registrationDate"
                      value={formData.registrationDate}
                      readOnly
                      className="w-full bg-transparent text-white outline-none cursor-default"
                      disabled={isSubmitting}
                    />
                    <span className="text-gray-400 text-sm">
                      {formatDisplayDate(formData.registrationDate)}
                    </span>
                  </div>
                  <div className="text-gray-400 text-sm mt-2 flex items-center gap-2">
                    <FaInfoCircle className="text-amber-400" />
                    Registration date is automatically set to today's date
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-amber-300 mb-2 font-bold">About Your Business *</label>
              <textarea
                name="aboutBusiness"
                value={formData.aboutBusiness}
                onChange={handleChange}
                required
                rows="4"
                className="w-full p-3 bg-gray-700 border-2 border-gray-600 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 text-white outline-none transition"
                placeholder="Briefly describe your business, products/services, and challenges"
                disabled={isSubmitting}
              />
            </div>

            <div className="mb-8">
              <label className="block text-amber-300 mb-2 font-bold">Query Type *</label>
              <select
                name="queryType"
                value={formData.queryType}
                onChange={handleChange}
                required
                className="w-full p-3 bg-gray-700 border-2 border-gray-600 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 text-white outline-none transition appearance-none"
                disabled={isSubmitting}
              >
                {data.queryTypes && data.queryTypes.length > 0 ? (
                  data.queryTypes.map((option) => (
                    <option key={option.value} value={option.value} className="bg-gray-800">
                      {option.label}
                    </option>
                  ))
                ) : (
                  <>
                    {/* <option value="general" className="bg-gray-800">General Consultation</option>
                    <option value="funding" className="bg-gray-800">Funding & Investment</option>
                    <option value="legal" className="bg-gray-800">Legal & Compliance</option>
                    <option value="marketing" className="bg-gray-800">Marketing & Sales</option>
                    <option value="technology" className="bg-gray-800">Technology Support</option> */}
                  </>
                )}
              </select>
            </div>

            {/* reCAPTCHA Section */}
            <div className="mb-8">
              <div className="flex justify-center">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={RECAPTCHA_SITE_KEY}
                  onChange={handleRecaptchaChange}
                  onExpired={handleRecaptchaExpired}
                  onErrored={handleRecaptchaError}
                  theme="dark"
                  size="normal"
                />
              </div>
              {/* {!recaptchaToken && !isSubmitting && (
                <p className="text-red-400 text-sm text-center mt-2">
                  Please complete the reCAPTCHA verification
                </p>
              )} */}
            </div>

            <div className="text-center">
              <button
                type="submit"
                disabled={isSubmitting || !recaptchaToken}
                className={`bg-gradient-to-r from-amber-500 to-yellow-500 text-gray-900 font-bold text-lg px-8 py-4 rounded-full transition-all duration-300 transform shadow-xl shadow-amber-500/20 flex items-center justify-center gap-3 mx-auto ${
                  isSubmitting || !recaptchaToken 
                    ? 'opacity-70 cursor-not-allowed' 
                    : 'hover:scale-105 hover:from-amber-600 hover:to-yellow-600'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    Register Now
                    <FaArrowRight />
                  </>
                )}
              </button>
              {submitSuccess && (
              <div className="bg-gradient-to-r from-green-900/30 to-green-800/30 border border-green-500/30 text-green-400 px-6 py-4 rounded-lg mb-6 backdrop-blur-sm">
                <p className="font-bold">
                  {data.formConfig?.successMessage?.title || 'Registration Successful!'}
                </p>
                <p>
                  {data.formConfig?.successMessage?.description || 'Confirmation email and Google Meet link will be sent shortly.'}
                </p>
              </div>
            )}
              <p className="text-gray-400 text-sm mt-4">
                By registering, you agree to our Terms of Service and Privacy Policy
              </p>
            </div>
          </form>
        </div>

        {/* Note Section */}
        {data.importantNotes && data.importantNotes.length > 0 && (
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 border-l-4 border-amber-500 p-6 rounded-r-lg">
            <h3 className="font-bold text-lg text-amber-300 mb-3 flex items-center gap-2">
              <FaCheckCircle className="text-amber-400" />
              Important Notes:
            </h3>
            <ul className="text-gray-300 space-y-3">
              {data.importantNotes.map((note, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-amber-400 rounded-full mt-2"></div>
                  <span>{note}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      
      <Footer />
    </div>
  );
}

export default MasaKalptaruPage;